'use client';

import { useCallback, useRef } from 'react';

/**
 * Drives the OffersFilter trigger "ripple" (the `::after` pulse) from a real
 * press instead of the CSS `:active` state.
 *
 * The ripple must both (a) never play when the page's CSS first loads and
 * (b) always animate to completion. A CSS `:active` animation fails both: on the
 * paid `/banking/m/` routes the async Panda stylesheet applying over the inlined
 * critical CSS can trigger it on load, and a quick tap ends `:active` before the
 * animation finishes, cutting it short.
 *
 * Instead a `data-rippling` attribute is toggled on the trigger: set on
 * activation (with a forced reflow so a rapid re-press restarts the animation
 * from the beginning) and removed on `animationend`. Activation is a primary
 * pointer press (`pointerdown`, button 0) or a keyboard press of Enter/Space
 * (`keydown`), so the ripple fires for keyboard users too now that it no longer
 * relies on `:active`.
 * Listeners are attached natively via a callback ref so this does not depend on
 * the base-ui trigger forwarding React synthetic handlers.
 *
 * Pair with a trigger whose `::after` runs the `ripple` keyframe scoped to
 * `&[data-rippling]::after`. Use `elementRef` if the caller also needs the node
 * (e.g. to call `.focus()`).
 */
export const useRipplePress = () => {
  const elementRef = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback((el: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    elementRef.current = el;
    if (!el) return;

    const startRipple = () => {
      el.removeAttribute('data-rippling');
      // Force a reflow so re-pressing restarts the animation from the beginning.
      void el.offsetWidth;
      el.setAttribute('data-rippling', 'true');
    };
    const startRippleOnPointer = (event: PointerEvent) => {
      // Primary button only (`button === 0`): right/middle presses open a
      // context menu or otherwise don't activate the control, so they shouldn't
      // ripple.
      if (event.button === 0) startRipple();
    };
    const startRippleOnKey = (event: KeyboardEvent) => {
      // `event.repeat` guards against a held key re-triggering every frame.
      if ((event.key === 'Enter' || event.key === ' ') && !event.repeat) startRipple();
    };
    const endRipple = (event: AnimationEvent) => {
      if (event.animationName === 'ripple') el.removeAttribute('data-rippling');
    };

    el.addEventListener('pointerdown', startRippleOnPointer);
    el.addEventListener('keydown', startRippleOnKey);
    el.addEventListener('animationend', endRipple);
    cleanupRef.current = () => {
      el.removeEventListener('pointerdown', startRippleOnPointer);
      el.removeEventListener('keydown', startRippleOnKey);
      el.removeEventListener('animationend', endRipple);
    };
  }, []);

  return { ref, elementRef };
};
